// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Button, Menu, Modal, Select, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { formatHumanName, normalizeErrorString } from '@medplum/core';
import type { DocumentReference, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconDotsVertical, IconFileText, IconNotes } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import {
  buildEncounterNotesDetailedCsv,
  downloadEncounterNotesPdfWithFileName,
  type EncounterNotePdfRecord,
  fetchPatientEncounterNotePdfData,
} from '../../utils/encounter-note-pdf';
import {
  buildPatientSelfIntakeHtml,
  buildScreenersExportText,
  downloadBlobFile,
  fetchPatientScreenerEntries,
  slugifyPatientName,
} from '../../utils/patient-export';
import { withTenantScope } from '../../utils/tenant';

function encodeBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

interface PatientExportMenuProps {
  patient: Patient;
  className?: string;
}

type EncounterExportFormat = 'csv' | 'pdf';

export function PatientExportMenu({ patient, className }: PatientExportMenuProps): JSX.Element {
  const medplum = useMedplum();
  const [exporting, setExporting] = useState<'screeners' | 'selfIntake' | 'notesCsv' | 'notesPdf' | null>(null);
  const [encounterExportFormat, setEncounterExportFormat] = useState<EncounterExportFormat | null>(null);
  const [encounterNotes, setEncounterNotes] = useState<EncounterNotePdfRecord[]>([]);
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);

  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient';
  const selectedEncounterNote = encounterNotes.find((note) => note.encounterId === selectedEncounterId);
  const encounterOptions = useMemo(
    () =>
      encounterNotes.map((note) => ({
        value: note.encounterId,
        label: `${note.title} • ${note.dateOfService}`,
      })),
    [encounterNotes]
  );

  const closeEncounterExportModal = (): void => {
    setEncounterExportFormat(null);
    setEncounterNotes([]);
    setSelectedEncounterId(null);
  };

  const handleExportScreeners = async (): Promise<void> => {
    if (!patient.id) {
      return;
    }

    setExporting('screeners');
    try {
      const entries = await fetchPatientScreenerEntries(medplum, patient.id);
      if (entries.length === 0) {
        showNotification({ color: 'yellow', message: 'No screener responses found for this patient.' });
        return;
      }

      downloadBlobFile(
        `${slugifyPatientName(patient)}-screeners.txt`,
        buildScreenersExportText(patient, entries),
        'text/plain;charset=utf-8'
      );
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setExporting(null);
    }
  };

  const openEncounterExportModal = async (format: EncounterExportFormat): Promise<void> => {
    if (!patient.id) {
      return;
    }

    setExporting(format === 'csv' ? 'notesCsv' : 'notesPdf');
    try {
      const notes = await fetchPatientEncounterNotePdfData(medplum, patient);
      if (notes.length === 0) {
        showNotification({ color: 'yellow', message: 'No encounter notes found for this patient.' });
        return;
      }

      setEncounterNotes(notes);
      setSelectedEncounterId(notes[0].encounterId);
      setEncounterExportFormat(format);
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setExporting(null);
    }
  };

  const handleExportSelfIntake = async (): Promise<void> => {
    if (!patient.id) {
      return;
    }

    setExporting('selfIntake');
    try {
      const [entries, coverages] = await Promise.all([
        fetchPatientScreenerEntries(medplum, patient.id),
        medplum.searchResources('Coverage', { beneficiary: `Patient/${patient.id}`, _count: '20' }),
      ]);
      if (entries.length === 0) {
        showNotification({ color: 'yellow', message: 'No intake responses found for this patient.' });
        return;
      }

      const html = buildPatientSelfIntakeHtml({ patient, entries, coverages });
      const fileName = `${slugifyPatientName(patient)}-patient-self-intake-summary.html`;
      const binary = await medplum.createBinary(
        new Blob([html], { type: 'text/html;charset=utf-8' }),
        fileName,
        'text/html'
      );
      const now = new Date().toISOString();
      const documentReference = withTenantScope<DocumentReference>({
        resourceType: 'DocumentReference',
        status: 'current',
        subject: { reference: `Patient/${patient.id}` },
        date: now,
        description: 'Generated patient self-intake summary for claims and external referrals.',
        type: { coding: [{ system: 'https://polarissleep.com/document-type', code: 'patient-self-intake-export' }] },
        content: [
          {
            attachment: {
              title: 'Patient Self-Intake Summary',
              url: `Binary/${binary.id}`,
              data: encodeBase64(html),
              contentType: 'text/html',
            },
          },
        ],
      });
      await medplum.createResource(documentReference);
      downloadBlobFile(fileName, html, 'text/html;charset=utf-8');
      showNotification({ color: 'green', message: 'Self-intake summary saved to Documents and downloaded.' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setExporting(null);
    }
  };

  const handleExportSelectedEncounter = async (): Promise<void> => {
    if (!selectedEncounterNote) {
      return;
    }

    try {
      if (encounterExportFormat === 'csv') {
        downloadBlobFile(
          `${slugifyPatientName(patient)}-${selectedEncounterNote.encounterId}-encounter-note.csv`,
          buildEncounterNotesDetailedCsv([selectedEncounterNote]),
          'text/csv;charset=utf-8'
        );
      }

      if (encounterExportFormat === 'pdf') {
        await downloadEncounterNotesPdfWithFileName(
          patient,
          [selectedEncounterNote],
          `${slugifyPatientName(patient)}-${selectedEncounterNote.encounterId}-encounter-note.pdf`
        );
      }

      closeEncounterExportModal();
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    }
  };

  return (
    <>
      <Menu position="bottom-end" shadow="md" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label={`Export records for ${patientName}`}
            className={className}
          >
            <IconDotsVertical size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Export</Menu.Label>
          <Menu.Item
            leftSection={<IconFileText size={16} />}
            onClick={handleExportScreeners}
            disabled={exporting !== null}
          >
            {exporting === 'screeners' ? 'Exporting screeners…' : 'Export Screeners'}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFileText size={16} />}
            onClick={() => handleExportSelfIntake().catch(() => undefined)}
            disabled={exporting !== null}
          >
            {exporting === 'selfIntake' ? 'Creating self-intake summary…' : 'Export Self-Intake Summary (HTML)'}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconNotes size={16} />}
            onClick={() => openEncounterExportModal('csv').catch(() => undefined)}
            disabled={exporting !== null}
          >
            {exporting === 'notesCsv' ? 'Loading encounter notes…' : 'Export Encounter Note (CSV)'}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconNotes size={16} />}
            onClick={() => openEncounterExportModal('pdf').catch(() => undefined)}
            disabled={exporting !== null}
          >
            {exporting === 'notesPdf' ? 'Loading encounter notes…' : 'Export Encounter Note (PDF)'}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={encounterExportFormat !== null}
        onClose={closeEncounterExportModal}
        title="Choose encounter note"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Choose which encounter note to export as {encounterExportFormat?.toUpperCase()}.
          </Text>
          <Select
            label="Encounter note to export"
            data={encounterOptions}
            value={selectedEncounterId}
            onChange={setSelectedEncounterId}
            searchable
            nothingFoundMessage="No encounter notes found"
          />
          <Button
            onClick={() => handleExportSelectedEncounter().catch(() => undefined)}
            disabled={!selectedEncounterId}
          >
            Export Selected Encounter
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
